import React, { FC } from "react";
import { LuChevronRight, LuRepeat } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { WorkDetailsModel } from "../../../../factories/work/model/work-details.model";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useGetRepeatsWorkQuery } from "../../../../slices/WorkSlice";
import { WorkTableSort } from "../../../works/types/work-table";
import { getSeriesStatus } from "./seriesStatus";

/** Repeat tab of a work item (path matches WorkPagesEnum.Repeats). */
const repeatTabPath = (workId: string) => `/work/${workId}/repeats`;

/** Is this work item's series recurring, how often, and when is the next one. */
export const useWorkSeriesStatus = (work: WorkDetailsModel | undefined) => {
  const { user } = useAppSelector((state) => state.appReducer);
  // Same arguments as the Repeat tab's default, so both share one request.
  const { data: series = [] } = useGetRepeatsWorkQuery(
    {
      orgId: getLinkedFirm()?.orgId || "",
      workId: work?.id || "",
      currentUserId: user.id,
      sortBy: WorkTableSort.StartDate,
      sortOrder: "desc",
    },
    { skip: !work?.id || !user.id },
  );
  return getSeriesStatus(series, work);
};

const RepeatIcon: FC<{ size: number; strokeWidth: number }> = (props) => (
  <LuRepeat {...props} />
);

/** "Recurring" pill next to the client's name (V49). */
export const RecurringBadge: FC<{ work: WorkDetailsModel }> = ({ work }) => {
  const navigate = useNavigate();
  const { isRecurring } = useWorkSeriesStatus(work);
  if (!isRecurring) return null;

  return (
    <button
      type={"button"}
      title={"On a recurring schedule"}
      onClick={() => navigate(repeatTabPath(work.id))}
      className={
        "inline-flex shrink-0 items-center gap-[5px] rounded-[20px] bg-[#F0EDFF] px-[11px] py-1 text-[12px] font-bold leading-[16px] text-[#6B4EFF] transition-colors hover:bg-[#E6E0FF]"
      }>
      <RepeatIcon size={12} strokeWidth={2.2} />
      Recurring
    </button>
  );
};

/** Recurring / Not recurring card on the Summary info card (V49). */
export const RecurringCard: FC<{ work: WorkDetailsModel }> = ({ work }) => {
  const navigate = useNavigate();
  const { isRecurring, cadence, nextText } = useWorkSeriesStatus(work);

  return (
    <button
      type={"button"}
      onClick={() => navigate(repeatTabPath(work.id))}
      className={
        isRecurring
          ? "flex w-full items-center gap-[11px] rounded-[10px] border border-[#E4DEFB] bg-[#F6F4FF] px-[13px] py-[11px] text-left transition-colors hover:border-[#CFC4F7]"
          : "flex w-full items-center gap-[11px] rounded-[10px] border border-[#E7E9EE] bg-[#FAFAFB] px-[13px] py-[11px] text-left transition-colors hover:border-[#D6D9E0]"
      }>
      <span
        className={
          isRecurring
            ? "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[#EAE4FF] text-[#6B4EFF]"
            : "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[#EFF0F3] text-[#8A90A2]"
        }>
        <RepeatIcon size={16} strokeWidth={2} />
      </span>
      <span className={"min-w-0 flex-1"}>
        <span className={"block text-[12.5px] font-extrabold text-[#1F2433]"}>
          {isRecurring ? `Recurring · ${cadence}` : "Not recurring"}
        </span>
        <span className={"mt-px block text-[11.5px] text-[#6B7185]"}>
          {isRecurring ? nextText : "Set up a repeat schedule"}
        </span>
      </span>
      <LuChevronRight
        size={14}
        strokeWidth={2.4}
        className={isRecurring ? "text-[#B3A6F2]" : "text-[#C2C7D2]"}
      />
    </button>
  );
};
